import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check, X, Building2, Users, Shield, Zap, ArrowRight, Star,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  TrendingUp, Flame, Phone, Mail, Eye, BarChart3, Award,
  Brain, Target, Landmark, GraduationCap, MapPin, Globe
} from "lucide-react";

const PartnershipPricingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (idx) => setOpenFaq(openFaq === idx ? null : idx);

  const faqs = [
    { q: "How is pricing calculated?", a: "Per student. You pay based on the number of active students using the platform." },
    { q: "What billing options are available?", a: "Monthly or Quarterly. Choose what suits your institute best." },
    { q: "Why is quarterly cheaper?", a: "Quarterly billing under a 6-month agreement offers better pricing for committed usage, allowing us to pass on the savings to you." },
    { q: "Is there a commitment period?", a: "Starter has no commitment — cancel anytime. Growth plans require a 6-month agreement for the best pricing." },
    { q: "How does white-label pricing work?", a: "White-label starts at ₹24,950/month covering up to 50 students with complete customised analytics." },
    { q: "Can I upgrade later?", a: "Yes. You can upgrade from Starter to Growth, or add white-label access at any time." },
  ];

  const comparisonRows = [
    { feature: "Full Platform Access", starter: true, growth: true },
    { feature: "Practice (Prelims + Mains)", starter: true, growth: true },
    { feature: "Answer Evaluation (AI)", starter: true, growth: true },
    { feature: "Daily Intelligence & Current Affairs", starter: true, growth: true },
    { feature: "Student Tracking", starter: "Basic", growth: "Advanced" },
    { feature: "Weak Student Detection", starter: false, growth: true },
    { feature: "Analytics Dashboard", starter: false, growth: true },
    { feature: "Institute Branding", starter: false, growth: true },
    { feature: "White-Label", starter: false, growth: "Available" },
    { feature: "Commitment", starter: "None", growth: "6 months" },
    { feature: "Support", starter: "Email", growth: "Priority" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="partnership-pricing-page">

      {/* ═══ HEADER ═══ */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700/50 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white tracking-tight">CLARIAS</span>
          </div>
          <a href="#plans" className="px-4 py-2 bg-white/10 text-white text-sm font-medium rounded-full border border-white/20 hover:bg-white/20 transition-all">
            View Plans
          </a>
        </div>
      </div>

      {/* ═══ SECTION 1: HERO ═══ */}
      <section className="pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6">
            Improve Student Results<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">with CLARIAS</span>
          </h1>
          <p className="text-lg text-slate-400 leading-relaxed mb-6 max-w-2xl mx-auto">
            Track preparation. Identify weak students early. Improve outcomes with a structured preparation intelligence system.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full">
            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
            <p className="text-sm text-blue-300 font-medium">
              CLARIAS is not a content platform. It is a preparation intelligence system for institutes.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2: WHO THIS IS FOR ═══ */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-sm font-semibold text-blue-400 uppercase tracking-widest mb-3 text-center">Built for</p>
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Who is this for?</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Landmark, label: "UPSC Coaching Institutes" },
              { icon: GraduationCap, label: "IAS Academies" },
              { icon: Building2, label: "Degree + UPSC Programs" },
              { icon: MapPin, label: "State PSC Coaching Centers" },
            ].map((item, idx) => (
              <div key={idx} className="flex flex-col items-center gap-3 p-5 bg-slate-800/50 rounded-2xl border border-slate-700 hover:border-blue-500/50 hover:bg-slate-800 transition-all group" data-testid={`audience-${idx}`}>
                <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                  <item.icon className="w-6 h-6 text-blue-400" />
                </div>
                <p className="text-sm font-semibold text-slate-200 text-center leading-tight">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 3: PRICING INTRO ═══ */}
      <section className="pb-10" id="plans">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-sm font-semibold text-blue-400 uppercase tracking-widest mb-3">Pricing</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Simple, Transparent Pricing</h2>
          <p className="text-lg text-slate-400 mb-2">No hidden fees. No complex setup.</p>
          <p className="text-sm text-slate-500 font-medium">Start monthly. Save more with quarterly billing.</p>
        </div>
      </section>

      {/* ═══ SECTION 4: THREE PLANS ═══ */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-5 items-start">

            {/* ── STARTER ── */}
            <div className="relative bg-slate-800/60 rounded-3xl border-2 border-slate-700 p-7 hover:border-blue-500/50 hover:shadow-xl hover:shadow-blue-500/10 transition-all" data-testid="starter-plan">
              <div className="mb-5">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-xs font-semibold mb-4">
                  <Zap className="w-3.5 h-3.5" /> FLEXIBLE
                </div>
                <h3 className="text-xl font-bold text-white mb-1">Starter</h3>
                <p className="text-sm text-slate-400">Perfect for institutes who want to start small and test CLARIAS.</p>
              </div>

              <div className="mb-5 pb-5 border-b border-slate-700">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold text-blue-400">₹899</span>
                  <span className="text-slate-500 text-sm">/ student / month</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">Billed monthly</p>
              </div>

              <ul className="space-y-2.5 mb-6">
                {[
                  "Full platform access",
                  "Daily intelligence & current affairs",
                  "Prelims + mains practice",
                  "Answer evaluation",
                  "Basic student tracking",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 bg-blue-500/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <Check className="w-3 h-3 text-blue-400" />
                    </div>
                    <span className="text-sm text-slate-300">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-slate-900/50 rounded-xl p-3.5 mb-5">
                <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1.5">Why choose Starter</p>
                <ul className="space-y-1">
                  {["No commitment", "Cancel anytime", "Ideal for pilot batches"].map((w, i) => (
                    <li key={i} className="text-xs text-slate-400 flex items-center gap-2">
                      <ChevronRight className="w-3 h-3 text-blue-500" /> {w}
                    </li>
                  ))}
                </ul>
              </div>

              <a href="https://wa.me/919154900228?text=Hi%2C%20I%27m%20interested%20in%20the%20CLARIAS%20Starter%20Plan%20for%20my%20institute." target="_blank" rel="noopener noreferrer"
                className="block w-full py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors text-sm text-center" data-testid="starter-cta">
                Start Small <ArrowRight className="w-4 h-4 inline ml-1" />
              </a>
            </div>

            {/* ── GROWTH MONTHLY (MOST POPULAR) ── */}
            <div className="relative bg-gradient-to-b from-blue-600 to-blue-700 rounded-3xl p-7 text-white shadow-2xl shadow-blue-600/20 md:scale-[1.04] border-2 border-blue-500/50" data-testid="growth-monthly-plan">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1.5 bg-amber-400 text-amber-900 text-xs font-bold rounded-full shadow-lg flex items-center gap-1">
                  <Star className="w-3 h-3" /> MOST POPULAR
                </span>
              </div>

              <div className="mb-5 mt-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 text-white rounded-full text-xs font-semibold mb-4">
                  <TrendingUp className="w-3.5 h-3.5" /> RECOMMENDED
                </div>
                <h3 className="text-xl font-bold mb-1">Growth</h3>
                <p className="text-sm text-blue-200">For institutes scaling their preparation systems.</p>
              </div>

              <div className="mb-5 pb-5 border-b border-white/20">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold">₹699</span>
                  <span className="text-blue-200 text-sm">/ student / month</span>
                </div>
                <p className="text-xs text-blue-300 mt-1">Billed monthly</p>
                <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 bg-white/10 rounded-full">
                  <Shield className="w-3 h-3 text-blue-300" />
                  <p className="text-xs text-blue-200 font-medium">6-month agreement</p>
                </div>
              </div>

              <ul className="space-y-2.5 mb-6">
                {[
                  "Everything in Starter",
                  "Advanced analytics dashboard",
                  "Weak student detection",
                  "Better performance tracking",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-blue-50">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3.5 mb-5">
                <p className="text-[11px] font-semibold text-blue-200 uppercase mb-1.5">Why choose Growth</p>
                <ul className="space-y-1">
                  {["6-month agreement for best pricing", "Balanced pricing", "Flexible monthly billing", "Ideal for growing institutes"].map((w, i) => (
                    <li key={i} className="text-xs text-blue-100 flex items-center gap-2">
                      <ChevronRight className="w-3 h-3 text-blue-300" /> {w}
                    </li>
                  ))}
                </ul>
              </div>

              <a href="https://wa.me/919154900228?text=Hi%2C%20I%27m%20interested%20in%20the%20CLARIAS%20Growth%20Plan%20(Monthly)%20for%20my%20institute." target="_blank" rel="noopener noreferrer"
                className="block w-full py-3 bg-white text-blue-700 font-bold rounded-xl hover:bg-blue-50 transition-colors text-sm shadow-lg text-center" data-testid="growth-monthly-cta">
                Start Growth Plan <ArrowRight className="w-4 h-4 inline ml-1" />
              </a>
            </div>

            {/* ── GROWTH QUARTERLY (BEST VALUE) ── */}
            <div className="relative bg-slate-800/60 rounded-3xl border-2 border-emerald-500/50 p-7 hover:shadow-xl hover:shadow-emerald-500/10 transition-all" data-testid="growth-quarterly-plan">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1.5 bg-emerald-500 text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1">
                  <Flame className="w-3 h-3" /> BEST VALUE
                </span>
              </div>

              <div className="mb-5 mt-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-xs font-semibold mb-4">
                  <Shield className="w-3.5 h-3.5" /> SAVE MORE
                </div>
                <h3 className="text-xl font-bold text-white mb-1">Growth <span className="text-emerald-400 text-base font-semibold">(Quarterly)</span></h3>
                <p className="text-sm text-slate-400">For institutes committed to improving student results.</p>
              </div>

              <div className="mb-5 pb-5 border-b border-slate-700">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold text-emerald-400">₹1,497</span>
                  <span className="text-slate-500 text-sm">/ student / quarter</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">That's just <span className="font-bold text-emerald-400">₹499/month</span> per student</p>
                <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full">
                  <p className="text-xs text-emerald-400 font-bold">Save ₹600 every 3 months</p>
                </div>
                <div className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 bg-slate-700/50 rounded-full">
                  <Shield className="w-3 h-3 text-slate-400" />
                  <p className="text-xs text-slate-300 font-medium">6-month agreement</p>
                </div>
              </div>

              <ul className="space-y-2.5 mb-6">
                {[
                  "Everything in Growth",
                  "Best pricing advantage",
                  "Stronger commitment",
                  "Better ROI",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 bg-emerald-500/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-sm text-slate-300">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3.5 mb-5">
                <p className="text-[11px] font-semibold text-emerald-400 uppercase mb-1.5">Why choose Quarterly</p>
                <ul className="space-y-1">
                  {["Lowest cost per student", "Better ROI for your institute", "Committed growth path"].map((w, i) => (
                    <li key={i} className="text-xs text-emerald-300/80 flex items-center gap-2">
                      <ChevronRight className="w-3 h-3 text-emerald-500" /> {w}
                    </li>
                  ))}
                </ul>
              </div>

              <a href="https://wa.me/919154900228?text=Hi%2C%20I%27d%20like%20to%20book%20a%20demo%20for%20the%20CLARIAS%20Growth%20Quarterly%20Plan." target="_blank" rel="noopener noreferrer"
                className="block w-full py-3 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 transition-colors text-sm text-center" data-testid="growth-quarterly-cta">
                Book Demo <ArrowRight className="w-4 h-4 inline ml-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 5: WHITE-LABEL / SUBDOMAIN ═══ */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-purple-900/40 to-indigo-900/40 border-2 border-purple-500/30 rounded-3xl p-8 md:p-10 relative overflow-hidden" data-testid="whitelabel-section">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />

            <div className="relative">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/15 text-purple-300 rounded-full text-xs font-semibold mb-4">
                    <Globe className="w-3.5 h-3.5" /> PREMIUM
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">White-Label / Subdomain Access</h3>
                  <p className="text-slate-400 text-sm mb-6 max-w-lg">
                    Offer CLARIAS under your institute's brand with a dedicated subdomain. Your students see your brand, your logo, your platform.
                  </p>

                  <ul className="space-y-2.5 mb-6">
                    {[
                      "Dedicated subdomain (yourinstitute.clarias.in)",
                      "Institute branding & logo",
                      "Full CLARIAS platform access",
                    ].map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <div className="w-5 h-5 bg-purple-500/15 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                          <Check className="w-3 h-3 text-purple-400" />
                        </div>
                        <span className="text-sm text-slate-300">{f}</span>
                      </li>
                    ))}
                  </ul>

                  <p className="text-xs text-slate-500 italic">
                    White-label access is designed for institutes committed to building their own branded learning platform.
                  </p>
                </div>

                <div className="md:w-72 flex-shrink-0">
                  <div className="bg-slate-800/80 border border-purple-500/30 rounded-2xl p-6 text-center">
                    <p className="text-xs font-semibold text-purple-400 uppercase tracking-wider mb-3">Starts at</p>
                    <div className="flex items-baseline justify-center gap-1 mb-1">
                      <span className="text-4xl font-extrabold text-white">₹24,950</span>
                      <span className="text-slate-400 text-sm">/ month</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">Covers up to 50 students</p>
                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3 mb-5">
                      <p className="text-xs text-purple-300">Complete customised analytics</p>
                    </div>
                    <a href="https://wa.me/919154900228?text=Hi%2C%20I%27m%20interested%20in%20the%20CLARIAS%20White-Label%20%2F%20Subdomain%20plan%20for%20my%20institute." target="_blank" rel="noopener noreferrer"
                      className="block w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all text-sm text-center" data-testid="whitelabel-cta">
                      Contact for White-Label <ArrowRight className="w-4 h-4 inline ml-1" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 6: VALUE SECTION ═══ */}
      <section className="py-20 border-y border-slate-700/50">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-sm font-semibold text-blue-400 uppercase tracking-widest mb-3">Platform Impact</p>
          <h2 className="text-3xl font-bold text-white mb-12">What CLARIAS Helps You Do</h2>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Eye, title: "Identify Weak Students Early", desc: "Spot struggling students before it's too late with pattern-based detection.", color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
              { icon: BarChart3, title: "Track Preparation Consistency", desc: "Monitor daily practice, answer quality, and study streaks across batches.", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
              { icon: Award, title: "Improve Answer Writing", desc: "Rubric evaluation helps students write better answers with structured feedback.", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
              { icon: Users, title: "Monitor Batch Performance", desc: "Dashboard view of entire batch progress, rankings, and trends.", color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" },
              { icon: TrendingUp, title: "Improve Results", desc: "Data-driven preparation leads to measurably better exam outcomes.", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
              { icon: Shield, title: "Build Institute Credibility", desc: "White-label platform with your branding builds trust with parents.", color: "text-slate-300", bg: "bg-slate-500/10", border: "border-slate-600" },
            ].map((item, idx) => (
              <div key={idx} className={`p-5 rounded-2xl ${item.bg} border ${item.border} hover:scale-[1.02] transition-all`}>
                <item.icon className={`w-7 h-7 ${item.color} mb-3`} />
                <h3 className="text-base font-bold text-white mb-1.5">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 6: COMPARISON TABLE ═══ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-sm font-semibold text-blue-400 uppercase tracking-widest mb-3 text-center">Compare</p>
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Feature Comparison</h2>

          <div className="rounded-2xl border border-slate-700 overflow-hidden bg-slate-800/30">
            <div className="grid grid-cols-3 bg-slate-800 border-b border-slate-700">
              <div className="p-5"><span className="text-sm font-semibold text-slate-400">Feature</span></div>
              <div className="p-5 text-center border-l border-slate-700">
                <span className="text-sm font-bold text-blue-400">Starter</span>
                <p className="text-xs text-slate-500">₹899/mo</p>
              </div>
              <div className="p-5 text-center border-l border-slate-700 bg-blue-500/5">
                <span className="text-sm font-bold text-emerald-400">Growth</span>
                <p className="text-xs text-slate-500">from ₹499/mo</p>
              </div>
            </div>
            {comparisonRows.map((row, idx) => (
              <div key={idx} className={`grid grid-cols-3 ${idx % 2 === 0 ? 'bg-slate-800/20' : 'bg-slate-800/40'} ${idx < comparisonRows.length - 1 ? 'border-b border-slate-700/50' : ''}`}>
                <div className="p-4 flex items-center"><span className="text-sm text-slate-300">{row.feature}</span></div>
                <div className="p-4 flex items-center justify-center border-l border-slate-700/50">
                  {row.starter === true ? (
                    <div className="w-6 h-6 bg-emerald-500/10 rounded-full flex items-center justify-center"><Check className="w-3.5 h-3.5 text-emerald-400" /></div>
                  ) : row.starter === false ? (
                    <div className="w-6 h-6 bg-slate-700/50 rounded-full flex items-center justify-center"><X className="w-3.5 h-3.5 text-slate-500" /></div>
                  ) : (
                    <span className="text-xs font-medium text-slate-400 bg-slate-700/50 px-2 py-1 rounded-md">{row.starter}</span>
                  )}
                </div>
                <div className="p-4 flex items-center justify-center border-l border-slate-700/50 bg-blue-500/5">
                  {row.growth === true ? (
                    <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center"><Check className="w-3.5 h-3.5 text-blue-400" /></div>
                  ) : row.growth === false ? (
                    <div className="w-6 h-6 bg-slate-700/50 rounded-full flex items-center justify-center"><X className="w-3.5 h-3.5 text-slate-500" /></div>
                  ) : (
                    <span className="text-xs font-medium text-blue-400 bg-blue-500/10 px-2 py-1 rounded-md">{row.growth}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 7: ENTERPRISE NOTE ═══ */}
      <section className="pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 flex items-center gap-5" data-testid="enterprise-note">
            <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-slate-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white mb-1">Need enterprise pricing?</p>
              <p className="text-sm text-slate-400">For institutes with large student volumes (500+), we offer customized pricing. <a href="https://wa.me/919154900228?text=Hi%2C%20I%27d%20like%20to%20discuss%20enterprise%20pricing%20for%20my%20institute%20(500%2B%20students)." target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 font-medium underline">Contact us for enterprise plans.</a></p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 8: WHY CLARIAS ═══ */}
      <section className="py-20 bg-slate-800/50 border-y border-slate-700/50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm font-semibold text-blue-400 uppercase tracking-widest mb-3">The difference</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12">Why CLARIAS?</h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { before: "Not just teaching", after: "Measuring preparation", color: "from-blue-500 to-cyan-500" },
              { before: "Not just content", after: "Improving performance", color: "from-purple-500 to-pink-500" },
              { before: "Not just tests", after: "Tracking execution", color: "from-amber-500 to-orange-500" },
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <p className="text-sm text-slate-500 line-through mb-3">{item.before}</p>
                <div className={`inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r ${item.color} rounded-full text-sm font-bold shadow-lg text-white`}>
                  {item.after}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 9: CTA ═══ */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="cta-heading">
            Ready to improve your institute's results?
          </h2>
          <p className="text-blue-200 text-lg mb-10">Join leading coaching institutes using CLARIAS to track, measure, and improve student preparation.</p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a href="https://wa.me/919154900228?text=Hi%2C%20I%27m%20interested%20in%20the%20CLARIAS%20Starter%20Plan%20for%20my%20institute." target="_blank" rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-blue-700 font-bold rounded-xl hover:bg-blue-50 transition-colors text-sm shadow-lg w-full sm:w-auto text-center" data-testid="cta-starter">
              Start with Starter Plan <ArrowRight className="w-4 h-4 inline ml-2" />
            </a>
            <a href="https://wa.me/919154900228?text=Hi%2C%20I%27d%20like%20to%20book%20a%20demo%20of%20CLARIAS%20for%20my%20coaching%20institute." target="_blank" rel="noopener noreferrer"
              className="px-8 py-4 bg-blue-500/30 text-white font-bold rounded-xl border-2 border-white/30 hover:bg-blue-500/50 transition-colors text-sm w-full sm:w-auto text-center" data-testid="cta-demo">
              Book a Demo <ArrowRight className="w-4 h-4 inline ml-2" />
            </a>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-blue-200">
            <a href="tel:+919154900228" className="flex items-center gap-2 hover:text-white transition-colors text-sm">
              <Phone className="w-4 h-4" /> +91 91549 00228
            </a>
            <a href="mailto:support@clarias.in" className="flex items-center gap-2 hover:text-white transition-colors text-sm">
              <Mail className="w-4 h-4" /> support@clarias.in
            </a>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 10: FAQ ═══ */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-sm font-semibold text-blue-400 uppercase tracking-widest mb-3 text-center">Questions</p>
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Frequently Asked Questions</h2>

          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border border-slate-700 rounded-xl overflow-hidden hover:border-blue-500/40 transition-colors" data-testid={`faq-${idx}`}>
                <button className="w-full flex items-center justify-between p-5 text-left" onClick={() => toggleFaq(idx)}>
                  <span className="text-sm font-semibold text-white">{faq.q}</span>
                  {openFaq === idx ? <ChevronUp className="w-5 h-5 text-blue-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-500 shrink-0" />}
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-sm text-slate-400 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="py-8 border-t border-slate-700/50">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-blue-400" />
            <span className="text-sm font-bold text-white">CLARIAS</span>
            <span className="text-xs text-slate-500">Preparation Intelligence System</span>
          </div>
          <p className="text-xs text-slate-500">&copy; 2026 CLARIAS. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PartnershipPricingPage;
